import { useState } from 'react';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CTASection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="cta" data-testid="cta-section" className="section-padding bg-vertex-gradient relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-vertex-radial" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-vertex-500/10 rounded-full blur-3xl" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-vertex-500/30 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left - Copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-vertex-500/10 border border-vertex-500/20 mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-vertex-300 text-sm font-semibold">Limited Spots Available</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white mb-6 leading-tight">
              Ready to Turn Your
              <br />
              <span className="gradient-text">Ad Spend Into Revenue?</span>
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              Book a free 30-minute strategy call with our senior team. We'll audit your current setup, identify quick wins, and map out a growth plan — even if you don't work with us.
            </p>

            <div className="space-y-4">
              {[
                'Free comprehensive audit of your marketing stack',
                'Custom growth roadmap tailored to your business',
                'No obligation — just pure strategic value',
                'Direct access to senior strategists, not salespeople',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-vertex-400 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Form */}
          <div className="glass rounded-3xl p-8 md:p-10">
            {submitted ? (
              <div className="text-center py-8">
                <div className="inline-flex p-4 rounded-full bg-emerald-500/20 mb-6">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-display font-bold text-white mb-3">You're In!</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Thank you for reaching out. A senior strategist from our team will contact you within 24 hours to schedule your strategy call.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-display font-bold text-white mb-2">Book Your Strategy Call</h3>
                <p className="text-gray-400 text-sm mb-8">Fill in your details and we'll be in touch within 24 hours.</p>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="Full Name *"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      data-testid="contact-name"
                      className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-vertex-500/50 focus:ring-1 focus:ring-vertex-500/50 transition-all duration-300"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Work Email *"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="contact-email"
                      className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-vertex-500/50 focus:ring-1 focus:ring-vertex-500/50 transition-all duration-300"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="company"
                      placeholder="Company Name"
                      value={formData.company}
                      onChange={handleChange}
                      data-testid="contact-company"
                      className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-vertex-500/50 focus:ring-1 focus:ring-vertex-500/50 transition-all duration-300"
                    />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="contact-phone"
                      className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-vertex-500/50 focus:ring-1 focus:ring-vertex-500/50 transition-all duration-300"
                    />
                  </div>
                  <textarea
                    name="message"
                    placeholder="Tell us about your business and marketing goals..."
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    data-testid="contact-message"
                    className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-vertex-500/50 focus:ring-1 focus:ring-vertex-500/50 transition-all duration-300 resize-none"
                  />

                  {error && (
                    <p className="text-red-400 text-sm" data-testid="contact-error">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="w-full btn-primary text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Get Your Free Strategy Session
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </button>
                  
                  <p className="text-gray-500 text-xs text-center mt-4">
                    No spam. No sales pressure. Just a conversation about growing your business.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
